import { AutoComplete, Button, Cascader, Form, Input, InputNumber, Modal, Select, Spin, message } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useEffect, useRef, useState } from "react";
import type { Ship, SpecialZoneReservationOrderUpdateParams } from "@zhihaoscm/entity";
import { ZONE_CONTRACT_GOODS } from "@zhihaoscm/meta";
import { shipService } from "@/api/services/shipService";
import { waybillService } from "@/api/services/waybillService";
import { areaLib, type AreaNode } from "@/utils/areaLib";
import { formatMoney } from "@/utils/money";
import { showRequestError } from "@/utils/requestErrorMessage";
import { onAddressInput, resolveTiandituSpecifyFromCityAdcode } from "@/views/pages/logistics/wharf";
import { DatePicker as ZhDatePicker } from "zhihao-ui-react";
import {
    getAvailableBalance,
    getContractDateRange,
    getContractTotalTon,
    getZoneLabel,
    loadZoneReservationOrderDetail,
    reservationEditTime,
    updateZoneReservationOrder,
    type DetailData
} from "../ZoneReservationOrderDetailPage";
import { ZONE_CONTRACT_UNIT_PRICE_MAX } from "./materialFormTypes";
import { zoneContractApi } from "./zoneContractReactApi";
import "./ZoneReservationOrderEditDialog.css";

type MaterialRow = { goodsCode?: number; plannedTonnage?: number; unitPrice?: number };
type EditFields = {
    materials: MaterialRow[];
    transportKey?: string;
    shipId?: string;
    mobile?: string;
    destinationPath?: string[];
    destinationPort?: string;
    reservationDate?: string;
};
type Option = { value: string; label: string; shipName?: string };
type AreaOption = { value: string; label: string; children?: AreaOption[] };

export interface ZoneReservationOrderEditDialogProps {
    reservationId: string;
    onCancel: () => void;
    onSaved: () => void | Promise<void>;
    loadDetail?: (id: string) => Promise<DetailData>;
    updateReservation?: (id: string, params: SpecialZoneReservationOrderUpdateParams) => Promise<unknown>;
}

/** 旧版编辑弹框的地区树：只开放原组件允许的省份。 */
const ALLOWED_PROVINCES = new Set(["安徽", "福建", "河北", "湖北", "湖南", "江苏", "江西", "上海", "浙江"]);
/** 后端旧预约单可能只有目的地文本，没有可在现行地区树里解析的市级编码。 */
const LEGACY_DESTINATION_VALUE = "__existing_destination__";

function areaOptions(nodes: AreaNode[], root = true): AreaOption[] {
    return nodes
        .filter(node => !root || ALLOWED_PROVINCES.has(node.name.replace(/省$|市$/, "")))
        .map(node => ({
            value: node.id,
            label: node.name,
            children:
                root && node.childs?.length
                    ? node.name.replace(/省$|市$/, "") === "上海"
                        ? [{ value: node.childs[0].id, label: "上海" }]
                        : node.childs.map(child => ({ value: child.id, label: child.name }))
                    : undefined
        }));
}

/** 用已有地区编号定位整条级联路径，避免回显后未修改地址时丢失地区。 */
function findAreaPath(options: AreaOption[], target: string, parent: string[] = []): string[] {
    for (const option of options) {
        const path = [...parent, option.value];
        if (option.value === target) return path;
        const child = findAreaPath(option.children || [], target, path);
        if (child.length) return child;
    }
    return [];
}

function areaPathNames(options: AreaOption[], path: string[]): string[] {
    let current = options;
    return path
        .map(id => {
            const found = current.find(option => option.value === id);
            current = found?.children || [];
            return found?.label || "";
        })
        .filter(Boolean);
}

/** 合同详情内编辑预约单：和 Vue 一样只打开局部弹框，提交成功后刷新原详情。 */
export function ZoneReservationOrderEditDialog({
    reservationId,
    onCancel,
    onSaved,
    loadDetail = loadZoneReservationOrderDetail,
    updateReservation = updateZoneReservationOrder
}: ZoneReservationOrderEditDialogProps) {
    const [form] = Form.useForm<EditFields>();
    const [detail, setDetail] = useState<DetailData | null>(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [ships, setShips] = useState<Option[]>([]);
    const [transports, setTransports] = useState<Option[]>([]);
    const [areas, setAreas] = useState<AreaOption[]>([]);
    const [portSuggestions, setPortSuggestions] = useState<Option[]>([]);
    const [destinationCleared, setDestinationCleared] = useState(false);
    const onCancelRef = useRef(onCancel);
    onCancelRef.current = onCancel;
    const materials = Form.useWatch("materials", form) || [];

    useEffect(() => {
        let disposed = false;
        void areaLib.ready.then(() => {
            if (!disposed) setAreas(areaOptions(areaLib.getAreaList()));
        });
        setLoading(true);
        void loadDetail(reservationId)
            .then(async value => {
                if (disposed) return;
                // 旧版 VO 缺合同物料或余额时补查合同，保持新增物料选项和余额回显完整。
                let merged = value;
                const contractId = Number(
                    value.specialZoneContract?.id || value.specialZoneReservationOrder.contractId
                );
                if ((!value.specialZoneContractGoods?.length || !value.specialZoneAccount) && contractId > 0) {
                    try {
                        const contract = await zoneContractApi.detail(contractId);
                        merged = {
                            ...value,
                            specialZoneContractGoods: value.specialZoneContractGoods?.length
                                ? value.specialZoneContractGoods
                                : contract.specialZoneContractGoods,
                            specialZoneAccount: value.specialZoneAccount || contract.specialZoneAccount
                        };
                    } catch {
                        /* 补查失败时仍允许用预约 VO 编辑。 */
                    }
                }
                if (disposed) return;
                setDetail(merged);
                const order = value.specialZoneReservationOrder;
                const shipName = order.shipName || value.ship?.cnname || value.ship?.name || "";
                const transportKey = order.transportOrderShipId
                    ? `1|${order.transportOrderShipId}`
                    : order.requirementPlatId
                      ? `2|${order.requirementPlatId}`
                      : undefined;
                setShips(
                    order.shipId ? [{ value: order.shipId, label: `${shipName} | ${order.shipId}`, shipName }] : []
                );
                setTransports(transportKey ? [{ value: transportKey, label: transportKey.split("|")[1] }] : []);
                form.setFieldsValue({
                    materials: value.specialZoneReservationOrderGoods?.length
                        ? value.specialZoneReservationOrderGoods.map(row => ({
                              goodsCode: Number(row.goodsCode),
                              plannedTonnage: Number(row.plannedTonnage),
                              unitPrice: Number(row.unitPrice)
                          }))
                        : [{}],
                    transportKey,
                    shipId: order.shipId || undefined,
                    mobile: order.mobile || "",
                    destinationPath: [],
                    destinationPort: order.destinationPort || "",
                    reservationDate: reservationEditTime(order.reservationTime).slice(0, 10)
                });
            })
            .catch(error => {
                if (!disposed) {
                    showRequestError(error, "加载预约单失败");
                    onCancelRef.current();
                }
            })
            .finally(() => {
                if (!disposed) setLoading(false);
            });
        return () => {
            disposed = true;
        };
    }, [form, loadDetail, reservationId]);

    useEffect(() => {
        if (!detail || destinationCleared) return;
        const areaId = detail?.specialZoneReservationOrder.destinationAddressAreaId;
        const path = areaId && areas.length ? findAreaPath(areas, areaId) : [];
        const existingAddress = detail.specialZoneReservationOrder.destinationAddress?.trim();
        // 有真实市级路径时优先使用路径；否则把历史地址回显为选中值，而非灰色 placeholder。
        form.setFieldValue("destinationPath", path.length ? path : existingAddress ? [LEGACY_DESTINATION_VALUE] : []);
    }, [areas, destinationCleared, detail, form]);

    /** 保留当前船名选项，同时用旧版的远程搜索接口补充候选项。 */
    async function searchShips(keyword: string) {
        if (!keyword.trim()) return;
        try {
            const response = await shipService.getShipSelector({ keyword: keyword.trim() });
            const next = (response.list || []).map((ship: Ship) => ({
                value: String(ship.id),
                label: `${ship.cnname || ship.name || ""} | ${ship.id}`,
                shipName: ship.cnname || ship.name || ""
            }));
            setShips(current => [...current.filter(option => option.value === form.getFieldValue("shipId")), ...next]);
        } catch (error) {
            showRequestError(error, "搜索船舶失败");
        }
    }

    async function searchTransports(keyword: string) {
        if (!keyword.trim()) return;
        try {
            const response = await waybillService.getSelectorTransportOrderRequirement({ keyword: keyword.trim() });
            const next = response.slice(0, 8).map(row => ({ value: `${row.type}|${row.id}`, label: String(row.id) }));
            setTransports(current => [
                ...current.filter(option => option.value === form.getFieldValue("transportKey")),
                ...next
            ]);
        } catch (error) {
            showRequestError(error, "搜索船运单失败");
        }
    }

    /** 按旧版所选城市请求目的港联想，选中后只提交最多十字地址。 */
    async function searchDestinationPorts(keyword: string) {
        const path = form.getFieldValue("destinationPath") as string[] | undefined;
        if (!keyword.trim() || !path?.[1]) {
            setPortSuggestions([]);
            return;
        }
        const specify = resolveTiandituSpecifyFromCityAdcode(path[1]);
        try {
            const result = (await onAddressInput(keyword.trim(), specify)) as
                | Array<{ value?: string; link?: { address?: string; name?: string } }>
                | undefined;
            setPortSuggestions(
                (result || [])
                    .map(item => {
                        const value = item.link?.address || item.link?.name || item.value || "";
                        return { value: value.slice(0, 10), label: value };
                    })
                    .filter(item => item.value)
            );
        } catch {
            // 旧版联想请求失败时保留手工输入目的港的能力。
            setPortSuggestions([]);
        }
    }

    /** 对齐旧版：物料、数量、单价和船舶字段在弹框里校验后原地提交。 */
    async function submit() {
        if (!detail || submitting) return;
        try {
            const fields = await form.validateFields();
            const rows = fields.materials || [];
            if (new Set(rows.map(row => row.goodsCode)).size !== rows.length) {
                message.warning("物料名称不能重复");
                return;
            }
            const order = detail.specialZoneReservationOrder;
            if (destinationCleared && !fields.destinationPath?.length) {
                message.warning("请选择目的地");
                return;
            }
            const ship = ships.find(option => option.value === fields.shipId);
            // 历史文本选项仅负责回显；用户未改动目的地时原样保留后端地址和地区编码。
            const path = (fields.destinationPath || []).filter(value => value !== LEGACY_DESTINATION_VALUE);
            const transport = fields.transportKey?.split("|");
            setSubmitting(true);
            await updateReservation(reservationId, {
                shipId: fields.shipId || "",
                shipName: ship?.shipName || order.shipName || "",
                mobile: fields.mobile?.trim() || undefined,
                destinationAddressAreaId: path[1] || order.destinationAddressAreaId || "",
                destinationAddress: path.length
                    ? areaPathNames(areas, path)
                          .map(name => name.replace(/省$|市$/, ""))
                          .join("")
                    : order.destinationAddress || "",
                destinationPort: fields.destinationPort?.trim() || undefined,
                goodsForms: rows.map(row => ({
                    goodsCode: Number(row.goodsCode),
                    plannedTonnage: Number(row.plannedTonnage),
                    unitPrice: Number(row.unitPrice)
                })),
                reservationTime: fields.reservationDate || "",
                requirementPlatId: transport?.[0] === "2" ? transport[1] : undefined,
                transportOrderShipId: transport?.[0] === "1" ? transport[1] : undefined
            });
            message.success("预约单已更新");
            await onSaved();
        } catch (error) {
            if (error && typeof error === "object" && "errorFields" in error) return;
            showRequestError(error, "预约单编辑失败");
        } finally {
            setSubmitting(false);
        }
    }

    const goods =
        detail?.specialZoneContractGoods?.filter(row => row.goodsCode !== 0 && row.goodsName !== "汇总") || [];
    const materialOptions = (goods.length ? goods : detail?.specialZoneReservationOrderGoods || []).map(row => ({
        value: Number(row.goodsCode),
        label:
            ("goodsName" in row ? row.goodsName : undefined) ||
            ZONE_CONTRACT_GOODS.getIntroByCode(Number(row.goodsCode)) ||
            String(row.goodsCode),
        unitPrice: Number(row.unitPrice || 0)
    }));
    const existingAddress = detail?.specialZoneReservationOrder.destinationAddress?.trim();
    const existingAreaId = detail?.specialZoneReservationOrder.destinationAddressAreaId;
    const hasKnownAreaPath = Boolean(existingAreaId && findAreaPath(areas, existingAreaId).length);
    // 地区树不包含历史地址时补一个只用于回显的选项，避免把已有值伪装成 placeholder。
    const destinationOptions: AreaOption[] = existingAddress && !hasKnownAreaPath
        ? [{ value: LEGACY_DESTINATION_VALUE, label: existingAddress }, ...areas]
        : areas;

    return (
        <Modal
            className="zone-reservation-order-edit-dialog"
            centered
            width={920}
            title="编辑预约单"
            open
            destroyOnHidden
            maskClosable={false}
            onCancel={onCancel}
            confirmLoading={submitting}
            onOk={() => void submit()}
            okText="确认"
            cancelText="取消"
        >
            <Spin spinning={loading}>
                {detail ? (
                    <Form form={form} layout="horizontal" className="zone-reservation-order-edit-dialog__body">
                        <section>
                            <h3>合同信息</h3>
                            <div className="zone-reservation-order-edit-dialog__grid">
                                <div>
                                    <span className="zone-reservation-order-edit-dialog__label">合同吨数</span>
                                    {getContractTotalTon(detail) === "--"
                                        ? "--"
                                        : `${formatMoney(Number(getContractTotalTon(detail).replace(/[^\d.]/g, "")))} 吨`}
                                </div>
                                <div>
                                    <span className="zone-reservation-order-edit-dialog__label">专区名称</span>
                                    {getZoneLabel(detail)}
                                </div>
                                <div>
                                    <span className="zone-reservation-order-edit-dialog__label">合同日期</span>
                                    {getContractDateRange(detail)}
                                </div>
                                <div>
                                    <span className="zone-reservation-order-edit-dialog__label">可预约金额</span>¥
                                    {getAvailableBalance(detail)}
                                </div>
                            </div>
                        </section>
                        <section>
                            <div className="zone-reservation-order-edit-dialog__section-head">
                                <h3>物料信息</h3>
                                <Button
                                    type="link"
                                    icon={<PlusOutlined />}
                                    onClick={() => form.setFieldValue("materials", [...materials, {}])}
                                >
                                    增加物料
                                </Button>
                            </div>
                            <div className="zone-reservation-order-edit-dialog__material-box">
                                <Form.List name="materials">
                                    {(fields, { remove }) =>
                                        fields.map((field, index) => (
                                            <div
                                                className="zone-reservation-order-edit-dialog__material"
                                                key={field.key}
                                            >
                                                <Form.Item
                                                    label="物料名称"
                                                    name={[field.name, "goodsCode"]}
                                                    rules={[{ required: true, message: "请选择物料名称" }]}
                                                >
                                                    <Select
                                                        placeholder="请选择物料"
                                                        options={materialOptions}
                                                        showSearch
                                                        optionFilterProp="label"
                                                        onChange={value => {
                                                            const option = materialOptions.find(
                                                                item => item.value === value
                                                            );
                                                            form.setFieldValue(
                                                                ["materials", index, "unitPrice"],
                                                                option?.unitPrice || 0
                                                            );
                                                        }}
                                                    />
                                                </Form.Item>
                                                <Form.Item
                                                    label="单价"
                                                    required={false}
                                                    name={[field.name, "unitPrice"]}
                                                    rules={[
                                                        {
                                                            required: true,
                                                            type: "number",
                                                            min: 0.000001,
                                                            max: ZONE_CONTRACT_UNIT_PRICE_MAX,
                                                            message: "请输入有效单价"
                                                        }
                                                    ]}
                                                >
                                                    <InputNumber
                                                        min={0}
                                                        max={ZONE_CONTRACT_UNIT_PRICE_MAX}
                                                        addonAfter="元"
                                                        style={{ width: "100%" }}
                                                    />
                                                </Form.Item>
                                                <Form.Item
                                                    label="计划提货量"
                                                    name={[field.name, "plannedTonnage"]}
                                                    rules={[
                                                        {
                                                            required: true,
                                                            type: "number",
                                                            min: 0.000001,
                                                            message: "请填写大于 0 的计划提货量"
                                                        }
                                                    ]}
                                                >
                                                    <InputNumber min={0} addonAfter="吨" style={{ width: "100%" }} />
                                                </Form.Item>
                                                <div className="zone-reservation-order-edit-dialog__preempt">
                                                    预占金额（元） ¥
                                                    {formatMoney(
                                                        Number(materials[index]?.plannedTonnage || 0) *
                                                            Number(materials[index]?.unitPrice || 0)
                                                    )}
                                                </div>
                                                {fields.length > 1 ? (
                                                    <Button
                                                        type="link"
                                                        icon={<DeleteOutlined />}
                                                        onClick={() => remove(field.name)}
                                                    >
                                                        删除物料
                                                    </Button>
                                                ) : null}
                                            </div>
                                        ))
                                    }
                                </Form.List>
                            </div>
                        </section>
                        <section>
                            <h3>船舶信息</h3>
                            <div className="zone-reservation-order-edit-dialog__grid">
                                <Form.Item label="关联船运需求/船运单" name="transportKey">
                                    <Select
                                        allowClear
                                        showSearch
                                        filterOption={false}
                                        onSearch={value => void searchTransports(value)}
                                        options={transports}
                                        placeholder="请输入关键字搜索船运单或需求"
                                    />
                                </Form.Item>
                                <Form.Item
                                    label="船名"
                                    name="shipId"
                                    rules={[{ required: true, message: "请选择船名" }]}
                                >
                                    <Select
                                        allowClear
                                        showSearch
                                        filterOption={false}
                                        onSearch={value => void searchShips(value)}
                                        options={ships}
                                        placeholder="请输入船名搜索"
                                    />
                                </Form.Item>
                                <Form.Item label="联系方式" name="mobile">
                                    <Input placeholder="请输入" />
                                </Form.Item>
                                <Form.Item
                                    label="目的地"
                                    name="destinationPath"
                                    required
                                    rules={[
                                        {
                                            required: !existingAddress || destinationCleared,
                                            message: "请选择目的地"
                                        }
                                    ]}
                                >
                                    <Cascader
                                        options={destinationOptions}
                                        showSearch
                                        placeholder="请选择省份 - 城市"
                                        onChange={value => {
                                            setDestinationCleared(!value?.length);
                                            form.setFieldValue("destinationPort", "");
                                            setPortSuggestions([]);
                                        }}
                                    />
                                </Form.Item>
                                <Form.Item
                                    label="目的港"
                                    name="destinationPort"
                                    rules={[{ max: 10, message: "目的港最多 10 个字" }]}
                                >
                                    <AutoComplete
                                        options={portSuggestions}
                                        onSearch={value => void searchDestinationPorts(value)}
                                        placeholder="请先选择目的地，再输入关键字搜索地址"
                                    />
                                </Form.Item>
                                <Form.Item
                                    label="预到日期"
                                    name="reservationDate"
                                    rules={[{ required: true, message: "请选择预到日期" }]}
                                >
                                    <ZhDatePicker type="date" valueFormat="YYYY-MM-DD" placeholder="请选择预到日期" />
                                </Form.Item>
                            </div>
                        </section>
                    </Form>
                ) : (
                    <div className="zone-reservation-order-edit-dialog__loading" />
                )}
            </Spin>
        </Modal>
    );
}
